/**
 * SWEPT MELEE vs DRONES — the spatial frame the contact resolves in.
 *
 * Lifted out of main.ts's fixed-tick block so it is importable (and testable) headless:
 * the temporal half of the test — sweeping the blade's baked arc across the tick's span —
 * lives in shared/hitVolume.ts and is exact. This module owns the OTHER half: WHICH FRAME
 * the attacker and the target are subtracted in.
 *
 * THE BUG THIS EXISTS TO KILL. The sweep runs on the FIXED 60 Hz TICK against `character.pos`,
 * which is fresh every tick. Drone positions (`DroneSwarm.sysPos`) are refreshed once per
 * RENDERED FRAME. In SYSTEM space that mismatch is fatal, because the frame itself is moving:
 * a planet's orbit + spin carries everything standing on it at 62–137 m/s. At a low frame rate
 * the accumulator drains several ticks per frame, so a fresh player was being compared against
 * a drone position tens of ms of carry away — metres of pure frame error. Measured: a drone at
 * 2.8 m hit at 48 fps and MISSED at 12 and 5 fps, with the rendered blade passing 2.05 m from it
 * (contact is 2.2 m). The blade visibly connected and dealt nothing.
 *
 * THE FIX — the architecture's own invariant (docs/hit-detection-research.md §3.8):
 *   "All hit resolution — bolt vs drone, bolt vs player, melee — happens in this body-LOCAL
 *    frame, because there the ~160 m/s surface carry cancels."
 * The server already resolves melee this way (rooms.ts applyMelee) and so does the bolt path
 * (server/src/projectiles.ts). This closes the last hole: resolve the client's melee-vs-drone
 * contact in the PLANET-LOCAL frame too. There the common carry cancels exactly — the drone's
 * anchor is a planet-local coordinate captured in the same frame its rendered position was, and
 * a planet-local coordinate does not age. Staleness stops meaning displacement.
 *
 * Targets keep a SYSTEM-space fallback (`pos`) for the cases with no body frame at all — a drone
 * in open space, or a player not on a planet — which is the identity case of the same math.
 */
import * as THREE from "three";
import { sweepArcVsCapsule, type HitArc } from "@hyperlight/shared";

/**
 * One drone as the swarm reports it. `pos` is SYSTEM space, refreshed on the RENDER frame;
 * `lp` is that same drone's PLANET-LOCAL anchor, captured in that same frame from that same
 * rendered planet pose (so it reproduces exactly where the drone was DRAWN — hit detection
 * must agree with the screen, not with some truer position the player never saw). `lp` is null
 * when the drone is not anchored to any body, and `pi` names the planet `lp` is expressed in.
 *
 * Both vectors are LIVE REFERENCES into the drone view — never copies. Do not retain.
 */
export interface MeleeTarget {
  id: number;
  pos: THREE.Vector3;
  lp: THREE.Vector3 | null;
  pi: number;
}

/** The planet's rotating frame AT THIS TICK (a StarSystem Planet's `pose` + its index). */
export interface BodyFrame {
  /** Index into StarSystem.planets — must match a target's `pi` for the local path to run. */
  index: number;
  center: THREE.Vector3;
  quat: THREE.Quaternion;
  invQuat: THREE.Quaternion;
}

// Per-tick / per-target scratch: this loop runs 60×/s over every live drone (see hit.ts).
const _charLocal = new THREE.Vector3();
const _invChar = new THREE.Quaternion();
const _localToChar = new THREE.Quaternion();
const _rel = new THREE.Vector3();

/**
 * Sweep `arc` over the active-window span [t0, t1] against every target, and collect the ones
 * the blade touched into `out` (cleared first; the same MeleeTarget objects, not copies).
 *
 * `charPos`/`charQuat` are the attacker's SYSTEM-space pose on THIS TICK — the exact quantities
 * streamed as PlayerState.lp/lr, so client prediction and server authority sweep the same space.
 * `frame` is the planet the attacker is standing on, posed on this same tick (null = none, in
 * which case everything falls back to system space).
 *
 * The arc is CHARACTER-LOCAL, so we bring each target into that frame rather than transforming
 * the arc — one composed quaternion per tick, not a rebuilt arc per target. With a body frame
 * that composition is conj(charQuat) ⊗ planetQuat: planet-LOCAL delta → CHARACTER-LOCAL. (That
 * is precisely the server's conj(lr) with lr = invPlanetQuat ⊗ charQuat — same frame, same math.)
 *
 * Allocation-free.
 */
export function sweepMeleeTargets(
  arc: HitArc,
  t0: number,
  t1: number,
  charPos: THREE.Vector3,
  charQuat: THREE.Quaternion,
  frame: BodyFrame | null,
  targets: readonly MeleeTarget[],
  targetRadius: number,
  skip: ReadonlySet<number>,
  out: MeleeTarget[],
): MeleeTarget[] {
  out.length = 0;
  _invChar.copy(charQuat).conjugate();
  if (frame !== null) {
    // The attacker in the PLANET-LOCAL frame, at THIS TICK's pose — so it is as fresh as
    // `character.pos` is, and (standing still) constant no matter how the frame rate wobbles.
    _charLocal.copy(charPos).sub(frame.center).applyQuaternion(frame.invQuat);
    _localToChar.copy(_invChar).multiply(frame.quat);
  }
  for (const d of targets) {
    if (skip.has(d.id)) continue;
    if (frame !== null && d.lp !== null && d.pi === frame.index) {
      // BODY-LOCAL. Both operands are planet-local coordinates, and a planet-local coordinate
      // does not age: the drone's is from the last RENDER frame and the player's is from THIS
      // TICK, yet the 62–137 m/s carry between those two instants is common to both frames and
      // subtracts out exactly. What is left is the true relative geometry — which is why the
      // same swing now lands the same verdict at 60, 12 and 5 fps. Rotate that local delta
      // straight into CHARACTER-LOCAL (the arc's own space) with the composed quaternion.
      _rel.copy(d.lp).sub(_charLocal).applyQuaternion(_localToChar);
    } else {
      // SYSTEM space — the identity case of the same math, for a target with no body frame
      // (a drone in open space, a player not on a planet). Nothing is carrying either of them,
      // so there is no carry to cancel and staleness costs only the target's own motion.
      _rel.copy(d.pos).sub(charPos).applyQuaternion(_invChar);
    }
    if (sweepArcVsCapsule(
      arc, t0, t1,
      _rel.x, _rel.y, _rel.z,
      _rel.x, _rel.y, _rel.z,
      targetRadius,
    ) < 0) continue;
    out.push(d);
  }
  return out;
}
